"use client";

import classnames from "classnames/bind";
import { CosmosButton } from "@cosmos/web/react";
import { FLAGS_API_PREFIX } from "../../../helpers/constants";
import { useObserveElementRef } from "../../../helpers/in-viewport/in-viewport";
import {
  BlocksHighlightProgressCarousel,
  HighlightTimestamp,
} from "../../../__generated__/contentful-types";
import { getContentfulVideoUrl } from "../../../helpers/getContentfulVideoUrl";
import { notNull } from "../../../helpers/notNull";
import { notNullish } from "../../../helpers/notNullish";
import { assertNotNullish } from "../../../helpers/assertNotNullish";
import { useEffect, useRef, Fragment, useState } from "react";
import { useViewportDimensions } from "../../../helpers/useViewportDimensions";
import { setVideoPlayback } from "../../../helpers/setVideoPlayback";
import { responsiveImageHelper } from "../../../helpers/responsiveImageHelper";
import { useImageLoading } from "../../../helpers/ImageLoadingContext";
import styles from "./highlight-progress-carousel.module.css";

const TABLET_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;
const TIMELINE_ITEM_WIDTH_MOBILE = 115;
const TIMELINE_ITEM_WIDTH_TABLET = 140;
const TIMELINE_ITEM_WIDTH_DESKTOP = 140;
const TIMELINE_WRAPPER_MAX_WIDTH = 1222;
const TIMESTAMP_OFFSET = 0.01;
const DEFAULT_TRANSITION_DURATION = "500ms";
const RESET_TRANSITION_DURATION = "0ms";

const cx = classnames.bind(styles);

export const HighlightProgressCarousel = ({
  block,
}: {
  block: BlocksHighlightProgressCarousel;
}) => {
  const { ref, isIntersecting } = useObserveElementRef<HTMLDivElement>({
    threshold: 0,
  });

  const highlightTimestampItems =
    block.highlightTimestampsCollection?.items.filter(notNull) || [];

  const [currentTimeState, setCurrentTimeState] = useState<number>(0);

  const activeItemIndex = highlightTimestampItems.findIndex(
    (timelineItem, index) =>
      isTimelineItemActive({
        block,
        isIntersecting,
        timelineItem,
        index,
        currentTimeState,
      }),
  );

  const activeTimelineItem = highlightTimestampItems[activeItemIndex];

  const activeContentItem = activeTimelineItem?.contentCollection?.items
    .filter(notNull)
    .find(({ timestamp }, contentItemIndex, items) => {
      const currentItemHasStarted = currentTimeState >= Number(timestamp);
      if (!currentItemHasStarted) {
        return false;
      }
      const nextContentItem = items[contentItemIndex + 1];
      return nextContentItem
        ? currentTimeState >= Number(timestamp) &&
            currentTimeState < Number(nextContentItem.timestamp)
        : currentTimeState >= Number(timestamp);
    });

  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const element = videoRef.current;
    if (element) {
      let animationFrameId: number;

      const tickVideo = () => {
        setCurrentTimeState(element.currentTime);
        // Loop:
        animationFrameId = window.requestAnimationFrame(() => tickVideo());
      };

      tickVideo();

      return () => window.cancelAnimationFrame(animationFrameId);
    }
  }, [block.sys.id, isIntersecting]);

  const [
    highlightProgressCarouselVideoPlayFrom,
    setHighlightProgressCarouselVideoPlayFrom,
  ] = useState<string>("0");

  useEffect(() => {
    const element = videoRef.current;
    if (element) {
      element.currentTime = Number(highlightProgressCarouselVideoPlayFrom);
    }
  }, [highlightProgressCarouselVideoPlayFrom, isIntersecting]);

  const { viewportWidth } = useViewportDimensions();

  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      setVideoPlayback(videoElement, isIntersecting ? "play" : "pause");
    }
  }, [isIntersecting]);

  const imageLoading = useImageLoading();

  return (
    <div className={cx("container")} ref={ref}>
      <div className={cx("inner")}>
        {highlightTimestampItems && (
          <Fragment>
            <div
              className={cx("navigation-overlay", "navigation-overlay--left")}
              onClick={() => {
                const previousItemTimestamp =
                  highlightTimestampItems[activeItemIndex - 1]?.timestamp;

                if (notNullish(previousItemTimestamp)) {
                  setHighlightProgressCarouselVideoPlayFrom(
                    previousItemTimestamp ===
                      highlightProgressCarouselVideoPlayFrom
                      ? String(Number(previousItemTimestamp) + TIMESTAMP_OFFSET)
                      : previousItemTimestamp,
                  );
                }
              }}
            />
            <div
              className={cx("navigation-overlay", "navigation-overlay--right")}
              onClick={() => {
                const nextItemTimestamp =
                  highlightTimestampItems[activeItemIndex + 1]?.timestamp;

                if (notNullish(nextItemTimestamp)) {
                  setHighlightProgressCarouselVideoPlayFrom(nextItemTimestamp);
                }
              }}
            />
          </Fragment>
        )}
        {highlightTimestampItems && (
          <div
            className={cx("timeline-wrapper")}
            style={{ maxWidth: TIMELINE_WRAPPER_MAX_WIDTH }}
          >
            <div
              className={cx("timeline")}
              style={{
                width: `calc(100% + ${getTimelineShiftedAmount({
                  activeItemIndex,
                  viewportWidth: viewportWidth,
                })}px`,
                left: `-${getTimelineShiftedAmount({
                  activeItemIndex,
                  viewportWidth: viewportWidth,
                })}px`,
                transitionDuration:
                  // go faster if video time less than second item
                  highlightTimestampItems[1] &&
                  currentTimeState <
                    Number(highlightTimestampItems[1].timestamp)
                    ? RESET_TRANSITION_DURATION
                    : DEFAULT_TRANSITION_DURATION,
              }}
            >
              {highlightTimestampItems.map((timelineItem, index) => (
                <a
                  key={index}
                  className={cx("timeline-item", {
                    "timeline-item--active": index === activeItemIndex,
                  })}
                  style={{
                    width: `${getTimelineItemWidth(viewportWidth)}px`,
                  }}
                  onClick={() => {
                    const timelineItemTimestamp = timelineItem.timestamp;

                    if (notNullish(timelineItemTimestamp)) {
                      setHighlightProgressCarouselVideoPlayFrom(
                        timelineItemTimestamp ===
                          highlightProgressCarouselVideoPlayFrom
                          ? String(
                              Number(timelineItemTimestamp) + TIMESTAMP_OFFSET,
                            )
                          : timelineItemTimestamp,
                      );
                    }
                  }}
                >
                  <div className={cx("timeline-progress-bar")}>
                    <div
                      className={cx("timeline-progress-bar-meter")}
                      style={{
                        transform: `scaleX(${getTimelineItemProgression({
                          block,
                          isIntersecting,
                          timelineItem,
                          index,
                          currentTimeState,
                        })})`,
                      }}
                    />
                  </div>
                  <span className={cx("timeline-tag")}>
                    {timelineItem.year}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
        <div className={cx("content")}>
          {isIntersecting && (
            <video
              className={cx(`video`)}
              preload="auto"
              autoPlay={true}
              playsInline={true}
              muted={true}
              loop={true}
              src={
                block.video?.url
                  ? getContentfulVideoUrl(block.video.url)
                  : undefined
              }
              ref={videoRef}
            />
          )}
          <div className={cx("overlay")} />
          {activeContentItem && (
            <div className={cx("text-wrap")}>
              <div className={cx("text")}>
                {activeContentItem.logo?.url && (
                  <img
                    className={cx("logo")}
                    loading={imageLoading}
                    srcSet={responsiveImageHelper.createContentfulSrcSet(
                      activeContentItem.logo.url,
                    )}
                    sizes="200px"
                    alt={activeContentItem.logo.description ?? ""}
                  />
                )}
                <h3 className={cx("title")}>
                  {activeContentItem.title && activeContentItem.title}
                </h3>
                {activeContentItem.countryIso &&
                  activeContentItem.countryName && (
                    <span className={cx("country-name")}>
                      {activeContentItem.countryIso && (
                        <img
                          className={cx("country-flag")}
                          src={`${FLAGS_API_PREFIX}${activeContentItem.countryIso}.png`}
                        />
                      )}
                      {activeContentItem.countryName}
                    </span>
                  )}
                <CosmosButton
                  href={`${block.buttonLink}#${activeTimelineItem.year}`}
                  kind="primary"
                  size="large"
                  className={cx("button")}
                >
                  {`${block.buttonText} ${activeTimelineItem.year}`}
                </CosmosButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function getTimelineItemWidth(viewportWidth: number) {
  if (viewportWidth < TABLET_BREAKPOINT) {
    return TIMELINE_ITEM_WIDTH_MOBILE;
  }
  if (viewportWidth < DESKTOP_BREAKPOINT) {
    return TIMELINE_ITEM_WIDTH_TABLET;
  }
  return TIMELINE_ITEM_WIDTH_DESKTOP;
}

function getTimelineItemProgression({
  block,
  isIntersecting,
  timelineItem,
  index,
  currentTimeState,
}: {
  block: BlocksHighlightProgressCarousel;
  isIntersecting: boolean;
  timelineItem: HighlightTimestamp;
  index: number;
  currentTimeState: number;
}) {
  if (
    !isTimelineItemActive({
      block,
      isIntersecting,
      timelineItem,
      index,
      currentTimeState,
    })
  ) {
    return 0;
  }

  const currentTimestamp = Number(timelineItem.timestamp);
  const nextItem = block.highlightTimestampsCollection?.items[index + 1];
  const currentTimeRemaining = currentTimeState - currentTimestamp;

  if (nextItem) {
    const nextTimestamp = Number(nextItem.timestamp);

    return currentTimeRemaining / (nextTimestamp - currentTimestamp);
  }

  return currentTimeRemaining / (Number(block.videoLength) - currentTimestamp);
}

function isTimelineItemActive({
  block,
  isIntersecting,
  timelineItem,
  index,
  currentTimeState,
}: {
  block: BlocksHighlightProgressCarousel;
  isIntersecting: boolean;
  timelineItem: HighlightTimestamp;
  index: number;
  currentTimeState: number;
}): boolean {
  const nextItemTimestamp =
    block.highlightTimestampsCollection?.items[index + 1]?.timestamp;

  /**
   * This `videoLength` property is a "required" field in Contentful, so it
   * should never be `null` in practice, but if it were then that would
   * introduce a logical error here. So, let's fail fast.
   */
  assertNotNullish(
    block.videoLength,
    `Block ${block.sys.id} has nullish "videoLength" property`,
  );

  /**
   * This `videoLength` property is a "required" field in Contentful, so it
   * should never be `null` in practice, but if it were then that would
   * introduce a logical error here. So, let's fail fast.
   */
  assertNotNullish(
    timelineItem.timestamp,
    `HighlightTimestamp ${timelineItem.sys.id} has nullish "timestamp" property`,
  );

  const timestampLowerBounds = Number(timelineItem.timestamp);
  const timestampUpperBounds = notNullish(nextItemTimestamp)
    ? Number(nextItemTimestamp)
    : Number(block.videoLength);

  return (
    isIntersecting &&
    currentTimeState >= timestampLowerBounds &&
    currentTimeState < timestampUpperBounds
  );
}

function getTimelineShiftedAmount({
  activeItemIndex,
  viewportWidth,
}: {
  activeItemIndex: number;
  viewportWidth: number;
}): number {
  const timelineBaseWidth =
    viewportWidth < TIMELINE_WRAPPER_MAX_WIDTH
      ? viewportWidth
      : TIMELINE_WRAPPER_MAX_WIDTH;
  const itemWidth = getTimelineItemWidth(viewportWidth);
  const itemsThatCanFitMinusOne = Math.floor(timelineBaseWidth / itemWidth) - 1;
  if (activeItemIndex > itemsThatCanFitMinusOne) {
    return (activeItemIndex - itemsThatCanFitMinusOne) * itemWidth;
  }
  return 0;
}
